
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Edit, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { ProductWithDetails } from '@/hooks/use-products-with-details';
import { useDeleteProduct } from '@/hooks/use-product-management';
import { toast } from '@/hooks/use-toast';
import { SortField, SortDirection } from './ProductTab';

interface ProductListProps {
  products: ProductWithDetails[];
  isLoading: boolean;
  onEdit: (product: ProductWithDetails) => void;
  onDeleted: () => void;
  hideDelete?: boolean;
  sortField?: SortField;
  sortDirection?: SortDirection;
  onSort?: (field: SortField) => void;
}

function SortIcon({ field, currentField, direction }: { field: SortField; currentField?: SortField; direction?: SortDirection }) {
  if (field !== currentField) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
  return direction === 'asc' 
    ? <ArrowUp className="h-3 w-3 ml-1" /> 
    : <ArrowDown className="h-3 w-3 ml-1" />;
}

export function ProductList({ products, isLoading, onEdit, onDeleted, hideDelete, sortField, sortDirection, onSort }: ProductListProps) {
  const deleteProduct = useDeleteProduct();

  const handleDeleteProduct = async (productName: string) => {
    try {
      await deleteProduct.mutateAsync(productName);
      toast({
        title: 'Product Removed',
        description: `${productName} has been removed successfully.`,
      });
      onDeleted();
    } catch (error: any) {
      console.error('Failed to delete product:', error);
      const isFkError = error?.code === '23503';
      toast({
        title: 'Cannot Delete Product',
        description: isFkError
          ? `${productName} is used in procurement or other records and cannot be deleted. Remove those references first.`
          : 'Failed to remove product. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">Loading products...</div>;
  }

  if (!products || products.length === 0) {
    return (
      <Card className="p-8 text-center text-muted-foreground">
        No products found.
      </Card>
    );
  }

  const sortableHeader = (label: string, field: SortField) => (
    <button
      className="flex items-center font-medium hover:text-foreground transition-colors"
      onClick={() => onSort?.(field)}
    >
      {label}
      <SortIcon field={field} currentField={sortField} direction={sortDirection} />
    </button>
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{sortableHeader('Product Name', 'name')}</TableHead>
          <TableHead>{sortableHeader('Category', 'category')}</TableHead>
          <TableHead>{sortableHeader('Unit of Measure', 'unit_of_measure')}</TableHead>
          <TableHead className="w-24">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {products.map((product) => (
          <TableRow key={product.id}>
            <TableCell>{product.name}</TableCell>
            <TableCell>{product.categories?.name || '-'}</TableCell>
            <TableCell>{product.unit_of_measure || 'kg'}</TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onEdit(product)}
                >
                  <Edit className="h-4 w-4 text-muted-foreground" />
                </Button>
                {!hideDelete && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDeleteProduct(product.name)}
                    disabled={deleteProduct.isPending}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
